import { motion, AnimatePresence } from "framer-motion";
import { ThumbsUp } from "lucide-react";
import { HeartsCascade } from "./ProductLogo";

/**
 * PackageTable — comparison table for Social Go vs Social Pro.
 * Pass `revealed=true` to expand the two new product rows (PresenTour, NarraTour)
 * with a slow accordion + fade the rest of the table back. The new rows pulse
 * pink to draw the eye.
 */

const baseRows = [
  { feature: "Social Images", go: "Included", pro: "Included" },
  { feature: "Tour Video", go: "Standard Edit (Portrait)", pro: "Bespoke Edit (Portrait)" },
  { feature: "Sneak Peek Reel", go: "Included", pro: "Included" },
  { feature: "Stories", go: "—", pro: "Branded Stories" },
];

const newRowsToReveal = [
  { feature: "PresenTour", featureNote: "**", go: "—", pro: "Included", highlight: true },
  { feature: "NarraTour", featureNote: "*", go: "—", pro: "Included", highlight: true },
];

const tailRows = [
  { feature: "Music & Pacing", go: "Standard", pro: "Premium Library" },
  { feature: "Social Crops & Collages", go: "Included", pro: "Included" },
  { feature: "Logo Branding", go: "Included", pro: "Included" },
];

const PINK = "#e84a78";
const PINK_BG = "rgba(232, 74, 120, 0.10)";
const PINK_BG_STRONG = "rgba(232, 74, 120, 0.18)";

function CellValue({ value, accent, isFeature = false, highlight = false }) {
  if (value === "—") {
    return <span className="text-white/30 text-2xl">—</span>;
  }
  if (value === "Included") {
    return (
      <span
        className="inline-flex items-center gap-2 text-base font-semibold tracking-tight"
        style={{ color: highlight ? PINK : accent }}
      >
        Included
      </span>
    );
  }
  return (
    <span
      className="text-base font-semibold tracking-tight"
      style={{ color: highlight ? PINK : isFeature ? "#e2e8f0" : accent }}
    >
      {value}
    </span>
  );
}

function FeatureCell({ name, note, highlight }) {
  return (
    <span
      className="text-base sm:text-lg font-semibold"
      style={{ color: highlight ? PINK : "#e2e8f0" }}
    >
      {name}
      {note ? (
        <span className="ml-0.5" style={{ color: highlight ? PINK : "#e2e8f0" }}>
          {note}
        </span>
      ) : null}
    </span>
  );
}

function Row({ row, delay = 0, highlightAnim = false, dimmed = false }) {
  return (
    <motion.div
      initial={{ opacity: 0, x: -16 }}
      animate={{
        opacity: dimmed ? 0.32 : 1,
        x: 0,
        filter: dimmed ? "saturate(0.6)" : "saturate(1)",
      }}
      transition={{
        opacity: { duration: 0.9, delay, ease: [0.16, 1, 0.3, 1] },
        x: { duration: 0.7, delay, ease: [0.16, 1, 0.3, 1] },
        filter: { duration: 0.9, delay },
      }}
      className="grid grid-cols-12 items-center px-7 py-4 border-b border-white/8 relative overflow-hidden"
      style={{ background: row.highlight ? PINK_BG : "transparent" }}
      data-testid={`package-row-${row.feature.toLowerCase().replace(/[^a-z]/g, "")}`}
    >
      {/* Highlight pulse glow on reveal */}
      {row.highlight && highlightAnim ? (
        <motion.span
          className="absolute inset-0 pointer-events-none"
          initial={{ background: "rgba(229, 0, 100, 0)" }}
          animate={{
            background: [
              "rgba(229, 0, 100, 0)",
              "rgba(229, 0, 100, 0.34)",
              PINK_BG_STRONG,
              PINK_BG,
            ],
          }}
          transition={{ duration: 1.6, delay: delay + 0.1, ease: "easeOut" }}
        />
      ) : null}

      <div className="col-span-4 relative">
        <FeatureCell
          name={row.feature}
          note={row.featureNote}
          highlight={row.highlight}
        />
      </div>
      <div className="col-span-4 relative text-center">
        <CellValue value={row.go} accent="#96C11F" highlight={row.highlight} />
      </div>
      <div className="col-span-4 relative text-center">
        <CellValue value={row.pro} accent="#e2e8f0" highlight={row.highlight} />
      </div>
    </motion.div>
  );
}

/**
 * AccordionRow — wraps `Row` in a height-animated container so revealed rows
 * "open up" smoothly rather than just popping in.
 */
function AccordionRow({ row, delay = 0, highlightAnim = false }) {
  return (
    <motion.div
      key={`acc-${row.feature}`}
      initial={{ height: 0, opacity: 0 }}
      animate={{ height: "auto", opacity: 1 }}
      exit={{ height: 0, opacity: 0 }}
      transition={{
        height: { duration: 1.1, delay, ease: [0.16, 1, 0.3, 1] },
        opacity: { duration: 0.9, delay: delay + 0.15 },
      }}
      className="overflow-hidden"
    >
      <Row row={row} delay={0} highlightAnim={highlightAnim} />
    </motion.div>
  );
}

export default function PackageTable({ revealed = false, "data-testid": testId }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 1.0, ease: [0.16, 1, 0.3, 1] }}
      className="rounded-2xl glass overflow-hidden"
      data-testid={testId}
    >
      {/* Header — Social Go + Social Pro logos as column headers (compact, single-row) */}
      <div className="grid grid-cols-12 px-7 py-5 bg-black/30 border-b border-white/12">
        <div className="col-span-4 flex items-center">
          <span className="kn-eyebrow text-white/55">Comparison</span>
        </div>
        <div className="col-span-4 flex items-center justify-center gap-3">
          <ThumbsUp className="w-5 h-5 shrink-0" fill="#96C11F" stroke="none" />
          <div
            className="kn-display text-lg sm:text-2xl leading-none whitespace-nowrap"
            style={{ color: "#96C11F", fontWeight: 900, letterSpacing: "-0.04em" }}
          >
            SOCIAL GO
          </div>
        </div>
        <div className="col-span-4 flex items-center justify-center gap-3">
          <HeartsCascade color="#e2e8f0" size={4} />
          <div
            className="kn-display text-lg sm:text-2xl leading-none whitespace-nowrap"
            style={{ color: "#e2e8f0", fontWeight: 900, letterSpacing: "-0.04em" }}
          >
            SOCIAL PRO
          </div>
        </div>
      </div>

      {/* Base rows — dim when the new rows are revealed so the eye lands on them */}
      <div>
        {baseRows.map((row, i) => (
          <Row
            key={`${row.feature}-${i}`}
            row={row}
            delay={0.4 + i * 0.06}
            dimmed={revealed}
          />
        ))}

        {/* Accordion-revealed new rows · slowly opens between base + tail */}
        <AnimatePresence initial={false}>
          {revealed
            ? newRowsToReveal.map((row, i) => (
                <AccordionRow
                  key={row.feature}
                  row={row}
                  delay={0.2 + i * 0.55}
                  highlightAnim
                />
              ))
            : null}
        </AnimatePresence>

        {tailRows.map((row, i) => (
          <Row
            key={`${row.feature}-tail-${i}`}
            row={row}
            delay={0.4 + (baseRows.length + i) * 0.06}
            dimmed={revealed}
          />
        ))}
      </div>

      {/* Footer note */}
      <div className="px-7 py-4 bg-black/20 border-t border-white/8">
        <div className="text-xs text-white/55 italic">
          Social Go and Social Pro are available exclusively as bolt-ons to NicheCom video products.
        </div>
        {revealed ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.4, duration: 0.8 }}
            className="mt-2 text-xs"
            style={{ color: PINK }}
            data-testid="package-table-footnote"
          >
            *NarraTour requires onboarding · **PresenTour requires agent onboarding (Avatar Day) · <span className="font-bold">Additional costs apply</span>
          </motion.div>
        ) : null}
      </div>
    </motion.div>
  );
}
